import { useState } from "react";
import { Link } from "react-router-dom";

import { devices, groups as groupsApi, platform, type DeviceSummary, type Group } from "../lib/api.js";
import { DeviceTree } from "../components/DeviceTree.js";
import { useAuth } from "../lib/auth-context.js";
import { displayStatus, relativeTime } from "../lib/freshness.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";
import { StatusBadge } from "../components/StatusBadge.js";

/**
 * Refresh interval. The poller's default is 60s, so this is fast enough that a
 * device changing state is visible within a poll or so, and slow enough that
 * an idle dashboard is not a meaningful query load.
 */
const REFRESH_MS = 30_000;

const VENDOR_LABEL: Record<string, string> = {
  mikrotik: "Mikrotik",
  morningstar: "Morningstar",
  tycon: "Tycon",
  generic: "Generic",
};

/** Remembered per browser: a view preference is not worth a round trip. */
const VIEW_KEY = "rr.deviceView";
const ALL_ORGS_KEY = "rr.allOrgs";

export function DeviceListPage() {
  const { user } = useAuth();
  const [view, setView] = useState<"tree" | "flat">(
    () => (localStorage.getItem(VIEW_KEY) === "flat" ? "flat" : "tree"),
  );
  // Off by default even for platform operators: seeing every organization at
  // once makes acting on the wrong one's device a real hazard, and the
  // everyday view should look like what everyone else sees.
  const [allOrgs, setAllOrgs] = useState(
    () => localStorage.getItem(ALL_ORGS_KEY) === "true",
  );
  const showAllOrgs = allOrgs && Boolean(user?.isPlatformAdmin);

  const { data: list, error, loadedAt, reload } = useLiveData<DeviceSummary[]>(
    () =>
      showAllOrgs
        ? platform.tree().then((r) =>
            r.devices.map((d) => ({
              ...d,
              // The platform tree spans organizations, so a device belongs to
              // its owner's node rather than being "shared with" anyone.
              is_shared: false,
              owner_name: r.organizations.find((o) => o.id === d.tenant_id)?.name,
            })) as unknown as DeviceSummary[],
          )
        : devices.list().then((r) => r.devices),
    REFRESH_MS,
    [showAllOrgs],
  );

  const { data: groupData, reload: reloadGroups } = useLiveData(
    () =>
      showAllOrgs
        ? platform.tree().then((r) => ({ groups: r.groups as Group[], canManage: false }))
        : groupsApi.list(),
    REFRESH_MS,
    [showAllOrgs],
  );

  const now = useNow();

  const setViewMode = (mode: "tree" | "flat") => {
    setView(mode);
    localStorage.setItem(VIEW_KEY, mode);
  };
  const setAllOrgsMode = (on: boolean) => {
    setAllOrgs(on);
    localStorage.setItem(ALL_ORGS_KEY, String(on));
  };

  // Distinct from `error`: that says the last attempt failed, this says the
  // data on screen has aged past the point of being worth presenting as
  // current, whatever the reason.
  const feedAge = loadedAt === null ? null : now - loadedAt;
  const feedStale = feedAge !== null && feedAge > REFRESH_MS * 3;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 style={{ fontSize: 15, margin: 0, letterSpacing: "0.14em" }}>&gt; DEVICES</h1>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          {loadedAt !== null && (
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.1em",
                color: feedStale || error ? "var(--status-warning)" : "var(--text-muted)",
              }}
            >
              {/* Named for what it is -- when the browser last heard from the
                  server -- so it is never confused with device poll age. */}
              {`UPDATED ${relativeTime(new Date(loadedAt).toISOString(), now)}`}
            </span>
          )}
          {user?.isPlatformAdmin && (
            <button
              onClick={() => setAllOrgsMode(!allOrgs)}
              style={{
                ...toggleStyle,
                color: showAllOrgs ? "var(--status-warning)" : "var(--text-muted)",
                borderColor: showAllOrgs ? "var(--status-warning)" : "var(--border)",
              }}
            >
              {showAllOrgs ? "[ ALL ORGS ]" : "[ THIS ORG ]"}
            </button>
          )}
          <button
            onClick={() => setViewMode(view === "tree" ? "flat" : "tree")}
            style={toggleStyle}
          >
            {view === "tree" ? "[ TREE ]" : "[ LIST ]"}
          </button>
          <Link to="/devices/new" style={addButtonStyle}>[ + ADD DEVICE ]</Link>
        </div>
      </div>

      {/* A failed refresh with data already on screen is a warning, not an
          error page: the rows below are still real, just older. */}
      {error && list && (
        <p style={{ color: "var(--status-warning)", fontSize: 12, marginTop: 0 }}>
          {`!! REFRESH FAILED (${error}) -- SHOWING LAST KNOWN DATA`}
        </p>
      )}
      {error && !list && <p style={{ color: "var(--status-critical)" }}>{`!! ${error}`}</p>}

      {!list && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

      {list && list.length === 0 && (
        <p style={{ color: "var(--text-muted)" }}>
          -- NO DEVICES -- <Link to="/devices/new">ADD ONE</Link>
        </p>
      )}

      {list && list.length > 0 && view === "tree" && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)", padding: 12 }}>
          <DeviceTree
            groups={groupData?.groups ?? []}
            devices={list}
            now={now}
            canManage={(groupData?.canManage ?? false) && !showAllOrgs}
            onChanged={() => { reloadGroups(); reload(); }}
          />
        </div>
      )}

      {list && list.length > 0 && view === "flat" && (
        <div
          style={{
            border: "1px solid var(--border)",
            overflow: "hidden",
            background: "var(--surface-1)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["NAME", "ADDRESS", "VENDOR", "STATUS", "LAST POLL"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((device) => (
                <tr key={device.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                  <td style={{ padding: "10px 14px" }}>
                    <Link
                      to={`/devices/${device.id}`}
                      style={{ color: "var(--text-primary)", fontWeight: 500, textDecoration: "none" }}
                    >
                      {device.name}
                    </Link>
                    {device.is_shared && (
                      <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11 }}>
                        {`shared by ${device.owner_name ?? "another org"}`}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text-secondary)" }}>{device.address}</td>
                  <td style={{ padding: "10px 14px", color: "var(--text-secondary)" }}>
                    {VENDOR_LABEL[device.vendor] ?? device.vendor}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <StatusBadge status={displayStatus(device, now)} />
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text-muted)" }}>
                    {relativeTime(device.last_poll_at, now)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const toggleStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--border)",
  padding: "5px 10px",
  fontSize: 11,
  color: "var(--text-secondary)",
  cursor: "pointer",
  letterSpacing: "0.08em",
};

const addButtonStyle: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: "0.1em",
  color: "var(--text-primary)",
  border: "1px solid var(--border)",
  padding: "6px 12px",
  textDecoration: "none",
};
